import Icon from '@components/Icon';
import Typography from '@components/Typography';
import { excluded, preferredCountriesOrder } from '@config/countries';
import { useFieldValidation } from '@ef-core/useFieldValidation';
import { type Validation } from '@ef-core/validation';
import { concatStyles } from '@styles/domain';
import { type FC, useId } from 'react';
import PhoneInput, {
  type Country,
  getCountries,
} from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import * as styles from './index.css';

interface Props {
  value?: string;
  onChange: (value: string) => void;
  validate?: (value?: string) => Validation | undefined;
  label?: string;
  id?: string;
  name?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
}

const countries = getCountries().filter(
  (country) => !excluded.includes(country),
);

const InputPhone: FC<Props> = ({
  value,
  onChange,
  validate,
  label,
  id,
  name,
  placeholder,
  disabled,
  required,
  className,
}) => {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const { validation, displayError, markTouched, markDirty } =
    useFieldValidation({ value, validate });

  return (
    <span className={concatStyles([styles.field, className])}>
      {label && (
        <label htmlFor={inputId}>
          <Typography variant='body3'>{label}</Typography>
          {required && (
            <span aria-hidden='true' className={styles.required}>
              *
            </span>
          )}
        </label>
      )}
      <span className={styles.inputWrap}>
        <PhoneInput
          id={inputId}
          name={name}
          placeholder={placeholder}
          disabled={disabled}
          aria-required={required || undefined}
          countrySelectProps={{ id: `${inputId}-country` }}
          international
          withCountryCallingCode
          limitMaxLength
          defaultCountry='FR'
          countries={countries}
          countryOptionsOrder={preferredCountriesOrder as Country[]}
          value={value}
          onChange={(next) => {
            markDirty();
            onChange(next ?? '');
          }}
          onBlur={markTouched}
          className={concatStyles([
            styles.phone,
            displayError && styles.inputError,
          ])}
        />
        {validation && (
          <Icon
            name={displayError ? 'close' : 'check'}
            size='sm'
            color={displayError ? 'error' : 'success'}
            className={styles.validationIcon}
          />
        )}
      </span>
      {displayError && validation?.error && (
        <Typography variant='body3' role='alert' className={styles.error}>
          {validation.error}
        </Typography>
      )}
    </span>
  );
};

export default InputPhone;
